/* Clovia collection / search filters.
   Upgrades the GET filter form to live updates via the Section Rendering API,
   with URL sync, back-button support and cancellation of stale requests. */

// Kept local on purpose: importing clovia.js by relative path would load a second
// copy (Shopify versions asset URLs with ?v=), re-running its side effects.
async function fetchSection(url, sectionId, signal) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u, { signal, headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(`Section ${sectionId} failed: ${res.status}`);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.panel = this.querySelector('[data-facets]');
    this.opener = this.querySelector('[data-facets-open]');

    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-facets-form], .toolbar__sort')) return;
      e.preventDefault();
      this.update(this.paramsFromForm());
    });
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel?.classList.contains('is-open')) this.close();
    });
    window.addEventListener('popstate', () => this.update(window.location.search.slice(1), false));
  }

  paramsFromForm() {
    const form = this.querySelector('[data-facets-form]');
    const params = new URLSearchParams();
    if (form) {
      for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
    } else {
      new URLSearchParams(window.location.search).forEach((v, k) => { if (k !== 'sort_by' && k !== 'page') params.append(k, v); });
    }
    const sort = this.querySelector('[data-sort]');
    if (sort && sort.value) params.set('sort_by', sort.value);
    return params.toString();
  }

  onChange = (e) => {
    if (!e.target.closest('[data-facets-form]') && !e.target.matches('[data-sort]')) return;
    clearTimeout(this.timer);
    const delay = e.target.type === 'number' ? 600 : 150;
    this.timer = setTimeout(() => this.update(this.paramsFromForm()), delay);
  };

  onClick = (e) => {
    const link = e.target.closest('[data-facet-link]');
    if (link) {
      e.preventDefault();
      const url = new URL(link.href, window.location.origin);
      this.update(url.searchParams.toString());
      if (link.closest('.pagination')) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-facets-open]')) return this.open();
    if (e.target.closest('[data-facets-close]')) return this.close();
  };

  open() {
    if (!this.panel) return;
    this.panel.classList.add('is-open');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.opener?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    this.panel.querySelector('button, input, summary')?.focus();
  }

  close() {
    if (!this.panel) return;
    this.panel.classList.remove('is-open');
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    this.opener?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    this.opener?.focus();
  }

  async update(query, push = true) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await fetchSection(url, this.sectionId, this.controller.signal);
      const swap = (sel) => {
        const next = doc.querySelector(sel);
        const current = this.querySelector(sel);
        if (next && current) current.replaceChildren(...next.childNodes);
      };
      swap('[data-results]');
      swap('[data-results-count]');
      // Refresh filter counts without losing which groups are open or the focused field.
      const nextForm = doc.querySelector('[data-facets-form]');
      const form = this.querySelector('[data-facets-form]');
      if (nextForm && form) {
        const openState = [...form.querySelectorAll('details')].map((d) => d.open);
        const focusedId = document.activeElement?.id;
        form.replaceChildren(...nextForm.childNodes);
        form.querySelectorAll('details').forEach((d, i) => { if (openState[i] === false) d.open = false; });
        if (focusedId) document.getElementById(focusedId)?.focus();
      }
      if (push) window.history.pushState({}, '', url);
    } catch (err) {
      if (err.name !== 'AbortError') window.location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

customElements.define('facet-filters', FacetFilters);
